import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { InvoiceSummary } from './types';

import { ApiError, apiRequest } from '@/lib/api-client';
import { env } from '@/lib/env';
import { useSession } from '@/lib/session';

interface InvoicesResponse {
  readonly invoices: readonly InvoiceSummary[];
}

/**
 * `GET /api/v1/merchant/invoices` — Sprint 15. `@RequirePermission(
 * 'orders:write')` server-side (an invoice is an order artefact; DEV §5
 * names no separate capability). Read-only: an invoice is created
 * automatically at checkout and never edited, so there is no mutation
 * hook here.
 */
export function useInvoicesQuery(): UseQueryResult<readonly InvoiceSummary[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['invoices', activeShopId],
    queryFn: async () => {
      const response = await apiRequest<InvoicesResponse>('/merchant/invoices', {
        accessToken,
        shopId: activeShopId,
      });
      return response.invoices;
    },
    enabled: accessToken !== null && activeShopId !== null,
  });
}

/**
 * `GET /api/v1/merchant/invoices/:id/pdf` needs `Authorization` +
 * `X-Shop-Id` headers, so a plain `<a href>` cannot reach it (supervisor
 * decision 2: no public URL). Fetch the bytes as a blob, hand the browser
 * an object URL for a one-shot download, then revoke it.
 */
export async function downloadInvoicePdf(
  invoiceId: string,
  invoiceNumber: string,
  accessToken: string | null,
  shopId: string | null,
): Promise<void> {
  const response = await fetch(`${env.apiBaseUrl}/api/v1/merchant/invoices/${invoiceId}/pdf`, {
    headers: {
      ...(accessToken !== null ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(shopId !== null ? { 'X-Shop-Id': shopId } : {}),
    },
  });
  if (!response.ok) {
    // An `ApiError` carrying the API's own `errorCode`, so the caller shows the translated message for THIS
    // refusal (not found, forbidden, server error) instead of a hard-coded sentence with a raw status number.
    const payload: unknown = await response.json().catch(() => null);
    const errorCode =
      typeof payload === 'object' && payload !== null && 'errorCode' in payload
        ? String(payload.errorCode)
        : undefined;
    throw new ApiError(
      `Invoice download failed (${String(response.status)}).`,
      response.status,
      payload,
      errorCode,
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `facture-${invoiceNumber}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
